import React, { useEffect, useState } from 'react';
import { Container, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert, Snackbar } from '@mui/material';
import { getRelativesList, createRelative } from '../../services/relativeService';
import { getClientList } from '../../services/clientService';
import AddRelativeDialog from '../../components/forms/AddRelativeDialog';
import { useAuth } from '../../contexts/AuthContext';
import EditRelativeDialog from '../../components/forms/Edit_forms/EditRelativeDialog';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { deleteRelative } from '../../services/relativeService';


function flattenErrors(obj, prefix = '') {
  let messages = [];
  for (const [key, value] of Object.entries(obj)) {
    const label = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      messages.push(`${label}: ${value.join(', ')}`);
    } else if (typeof value === 'object' && value !== null) {
      messages = messages.concat(flattenErrors(value, label));
    } else {
      messages.push(`${label}: ${value}`);
    }
  }
  return messages;
}

const RelativesPage = () => {
  const [relatives, setRelatives] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const { user } = useAuth();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);


  const fetchRelatives = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getRelativesList();
      setRelatives(data);
    } catch (e) {
      setError('Failed to load relatives list.');
    }
    setLoading(false);
  };

  const handleMenuOpen = (e, relative) => {
    setSelected(relative);
    setMenuAnchorEl(e.currentTarget);
  };
  
  const handleMenuClose = () => setMenuAnchorEl(null);
  
  const handleEdit = () => {
    setEditOpen(true);
    handleMenuClose();
  };
  
  const handleDelete = async () => {
    handleMenuClose();
    const confirmed = window.confirm(`Delete relative "${selected.relative_name}"?`);
    if (!confirmed) return;
  
    try {
      await deleteRelative(selected.id || selected.relative_id);
      setSnackbar({ open: true, message: 'Relative deleted.', severity: 'success' });
      fetchRelatives();
    } catch (err) {
      setSnackbar({ open: true, message: 'Delete failed.', severity: 'error' });
    }
  };
  

  const fetchClients = async () => {
    try {
      const data = await getClientList();
      setClients(data);
    } catch (e) {
      console.error('Failed to fetch clients:', e); // optional
    }
  };

  useEffect(() => {
    fetchRelatives();
    fetchClients();
  }, []);

  const handleAddRelative = async (form) => {
    try {
      await createRelative(form);
      setSnackbar({ open: true, message: 'Relative added successfully!', severity: 'success' });
      setAddOpen(false);
      fetchRelatives();
    } catch (err) {
      let message = 'Failed to add relative';
      if (err.response && err.response.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          message = data;
        } else if (typeof data === 'object') {
          message = flattenErrors(data).join(' | ');
        }
      }
      setSnackbar({ open: true, message, severity: 'error' });
      throw new Error(message);
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>Relatives Management</Typography>
      {user?.role === 'admin' && (
              <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setAddOpen(true)}>Add Relative</Button>

            )}
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Tel No</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Client</TableCell>
                  {user?.role === 'admin' && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {relatives.map((r) => (
                  <TableRow key={r.relative_id || r.id}>
                    <TableCell>{r.relative_id || r.id}</TableCell>
                    <TableCell>{r.relative_name}</TableCell>
                    <TableCell>{r.tel_no}</TableCell>
                    <TableCell>{r.email || '-'}</TableCell>
                    <TableCell>{r.client}</TableCell>
                    {user?.role === 'admin' && (
                      <TableCell>
                        <IconButton onClick={(e) => handleMenuOpen(e, r)}>
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      <EditRelativeDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onEdit={fetchRelatives}
        initialData={selected}
        clients={clients}
      />
      <AddRelativeDialog open={addOpen} onClose={() => setAddOpen(false)} onAdd={handleAddRelative} clients={clients} />
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} />
    </Container>
  );
};

export default RelativesPage; 
